import React, { useState, useEffect } from 'react';
import { 
  Loader2, DollarSign, Users, Car, Utensils, Home, Target, Package, Calendar
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import PDFShareButton from '../common/PDFShareButton';

const categoryColors = {
  transport: { icon: Car, color: 'text-blue-500', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  meal: { icon: Utensils, color: 'text-orange-500', bgColor: 'bg-orange-100 dark:bg-orange-900/30' },
  accommodation: { icon: Home, color: 'text-green-500', bgColor: 'bg-green-100 dark:bg-green-900/30' },
  activity: { icon: Target, color: 'text-purple-500', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
  other: { icon: Package, color: 'text-slate-500', bgColor: 'bg-slate-100 dark:bg-slate-800' },
};

const CostBar = ({ member, maxCost }) => {
  const percentage = maxCost > 0 ? (member.total_cost / maxCost) * 100 : 0;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl">{member.avatar}</span>
          <div>
            <p className="font-medium text-slate-900 dark:text-white text-sm">{member.name}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {member.total_days} dia(s)
            </p>
          </div>
        </div>
        <p className="font-bold text-orange-600 dark:text-orange-400">
          R$ {member.total_cost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </p>
      </div>
      <Progress value={percentage} className="h-2" />
      
      {/* Cost breakdown */}
      <div className="flex flex-wrap gap-2">
        {member.transport_cost > 0 && (
          <Badge variant="outline" className="text-xs flex items-center gap-1">
            <Car className="w-3 h-3 text-blue-500" />
            R$ {member.transport_cost.toFixed(2)}
          </Badge>
        )}
        {member.meal_cost > 0 && (
          <Badge variant="outline" className="text-xs flex items-center gap-1">
            <Utensils className="w-3 h-3 text-orange-500" />
            R$ {member.meal_cost.toFixed(2)}
          </Badge>
        )}
        {member.accommodation_cost > 0 && (
          <Badge variant="outline" className="text-xs flex items-center gap-1">
            <Home className="w-3 h-3 text-green-500" />
            R$ {member.accommodation_cost.toFixed(2)}
          </Badge>
        )}
        {member.activity_cost > 0 && (
          <Badge variant="outline" className="text-xs flex items-center gap-1">
            <Target className="w-3 h-3 text-purple-500" />
            R$ {member.activity_cost.toFixed(2)}
          </Badge>
        )}
        {member.other_cost > 0 && (
          <Badge variant="outline" className="text-xs flex items-center gap-1">
            <Package className="w-3 h-3 text-slate-500" />
            R$ {member.other_cost.toFixed(2)}
          </Badge>
        )}
      </div>
    </div>
  );
};

export const CostSummary = ({ tripId, compact = false }) => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchCostSummary();
  }, [tripId]);

  const fetchCostSummary = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/trips/${tripId}/cost-summary`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching cost summary:', error);
      toast.error('Erro ao carregar resumo de custos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!data || data.members.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <DollarSign className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">
            Nenhum custo calculado ainda
          </p>
          <p className="text-sm text-slate-400 mt-1">
            Adicione atividades com custo no roteiro
          </p>
        </CardContent>
      </Card>
    );
  }

  const maxCost = Math.max(...data.members.map(m => m.total_cost));
  const currentUserCost = data.members.find(m => m.user_id === user?.id);

  if (compact) {
    // Compact view - show only current user
    return (
      <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2 text-green-700 dark:text-green-400">
            <DollarSign className="w-5 h-5" />
            Seu Custo Estimado
          </CardTitle>
        </CardHeader>
        <CardContent>
          {currentUserCost ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-600 dark:text-slate-400">
                  Baseado em {currentUserCost.total_days} dia(s)
                </span>
                <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                  R$ {currentUserCost.total_cost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {currentUserCost.transport_cost > 0 && (
                  <Badge variant="outline" className="text-xs">
                    <Car className="w-3 h-3 mr-1 text-blue-500" />
                    Transporte: R$ {currentUserCost.transport_cost.toFixed(2)}
                  </Badge>
                )}
                {currentUserCost.meal_cost > 0 && (
                  <Badge variant="outline" className="text-xs">
                    <Utensils className="w-3 h-3 mr-1 text-orange-500" />
                    Refeições: R$ {currentUserCost.meal_cost.toFixed(2)}
                  </Badge>
                )}
                {currentUserCost.accommodation_cost > 0 && (
                  <Badge variant="outline" className="text-xs">
                    <Home className="w-3 h-3 mr-1 text-green-500" />
                    Hospedagem: R$ {currentUserCost.accommodation_cost.toFixed(2)}
                  </Badge>
                )}
                {currentUserCost.activity_cost > 0 && (
                  <Badge variant="outline" className="text-xs">
                    <Target className="w-3 h-3 mr-1 text-purple-500" />
                    Atividades: R$ {currentUserCost.activity_cost.toFixed(2)}
                  </Badge>
                )}
              </div>
            </div>
          ) : (
            <p className="text-slate-500">Você ainda não está na lista de custos</p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full view with all members (organizer view)
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              Resumo de Custos por Membro
            </CardTitle>
            <CardDescription>
              Custo estimado baseado nos dias de presença de cada membro
            </CardDescription>
          </div>
          {data.is_organizer && (
            <PDFShareButton
              endpoint={`/trips/${tripId}/cost-summary/pdf`}
              filename="custos-viagem.pdf"
              title="Resumo de Custos - ViajeJunto"
              description={`Custo total: R$ ${data.total_trip_cost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
              buttonText="Compartilhar"
              buttonVariant="outline"
              buttonSize="sm"
              className="rounded-full border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-900/40"
            />
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Total */}
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between">
            <span className="text-green-700 dark:text-green-400 font-medium">
              Custo Total da Viagem
            </span>
            <span className="text-2xl font-bold text-green-600 dark:text-green-400">
              R$ {data.total_trip_cost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        {/* Members */}
        <div className="space-y-4">
          {data.members.map((member, index) => (
            <div
              key={member.user_id}
              className={`p-4 rounded-lg border ${
                member.user_id === user?.id 
                  ? 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/10' 
                  : 'border-slate-200 dark:border-slate-700'
              }`}
            >
              <CostBar member={member} maxCost={maxCost} />
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Legenda:</p>
          <div className="flex flex-wrap gap-3">
            <span className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
              <Car className="w-3 h-3 text-blue-500" /> Transporte
            </span>
            <span className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
              <Utensils className="w-3 h-3 text-orange-500" /> Refeições
            </span>
            <span className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
              <Home className="w-3 h-3 text-green-500" /> Hospedagem
            </span>
            <span className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
              <Target className="w-3 h-3 text-purple-500" /> Atividades
            </span>
            <span className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
              <Package className="w-3 h-3 text-slate-500" /> Outros
            </span>
          </div>
        </div>

        {!data.is_organizer && (
          <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
            Você está vendo apenas seu próprio custo estimado
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default CostSummary;
